import type { UIInventoryItem, UIPartyCharacter } from '@sorrem-client/types';
import { useOptionalConfirmContext } from '@sorrem-client/ui/components';
import { useItemTrade } from '@sorrem-client/ui/hooks';
import { toEssencePool, resolveEssenceExchangeValue, resolveItemEssenceValue, type EssencePool } from '@sorrem/shared';
import { useCallback } from 'react';
import { resolveCharacterHomeItemCard } from './character-home-item-moves';

/** What a house of exchange pays for an item: the essence it is worth. */
export function resolveTradeEssence(item: Pick<UIInventoryItem, 'essenceValue' | 'instance'>): EssencePool {
    return toEssencePool(resolveItemEssenceValue({ essenceValue: item.essenceValue ?? {}, ...(item.instance ? { instance: item.instance } : {}) }));
}

interface CharacterHomeTrade {
    /** Whether trading can be asked for here: it needs a confirmation prompt to ask through. */
    readonly canTrade: boolean;
    readonly isTrading: boolean;
    readonly error: string | null;
    /**
     * Asks to trade one of the character's items to a house of exchange, and
     * trades it if they confirm. An equipped item has to be taken off first.
     * Resolves whether it was traded.
     */
    readonly tradeItem: (itemId: string) => Promise<boolean>;
}

/** Trading Character Home items, from the Inventory tab's panel or an item's menu (#267). */
export function useCharacterHomeTrade(character: UIPartyCharacter | null): CharacterHomeTrade {
    // Optional: the prompt is mounted at the app root, but not around every test.
    const confirmContext = useOptionalConfirmContext();
    const { isTrading, error, trade } = useItemTrade(character?.id ?? null);

    const tradeItem = useCallback(async (itemId: string): Promise<boolean> => {
        const card = character === null ? undefined : resolveCharacterHomeItemCard(character, itemId);
        const item = card?.item;
        if (confirmContext === undefined || item === undefined || card?.isEquipped) {
            return false;
        }
        const confirmed = await confirmContext.confirm({
            title: `Trade ${item.name}?`,
            description: `A house of exchange takes it for ${resolveEssenceExchangeValue(resolveTradeEssence(item))} essence value, poured into your vessel. The item is gone for good.`,
            confirmLabel: 'Trade',
            cancelLabel: 'Keep',
        });
        return confirmed && await trade(item.id);
    }, [character, confirmContext, trade]);

    return { canTrade: confirmContext !== undefined, isTrading, error, tradeItem };
}
